import {
  ExceptionReasonFinalState,
  ExceptionReattemptConfig,
  DayType,
  PodRequirementAtStop,
  ExceptionReasonDriverAssignmentType,
} from "@api/graphql/generated/generated-types";

// Exhaustive by type: adding a value to ExceptionReasonFinalState in cube will
// break this build until a label is supplied here.
export const FINAL_STATE_LABELS: Record<ExceptionReasonFinalState, string> = {
  [ExceptionReasonFinalState.Completed]: "Completed",
  [ExceptionReasonFinalState.Canceled]: "Canceled",
  [ExceptionReasonFinalState.UnassignedAndHold]: "Unassigned and hold",
};

export function getExceptionReasonFinalStateLabel(
  finalState: ExceptionReasonFinalState
): string {
  // The fallback is unreachable for a well-typed caller, but a server rolling
  // out a new enum value ahead of the client would otherwise render "undefined".
  return FINAL_STATE_LABELS[finalState] ?? "-";
}

export function getReattemptCountLabel(
  reattemptConfigs: ExceptionReattemptConfig[] | null | undefined
): string {
  if (!reattemptConfigs || reattemptConfigs.length === 0) return "None";
  return String(reattemptConfigs.length);
}

export function getDescriptionLabel(description?: string | null): string {
  if (!description) return "-";
  return description;
}

// Title-case labels for select options. These are the strings the user picks
// from, and they are reused verbatim in the table tooltip so the two views can
// never disagree about what a value is called.
export const DAY_TYPE_LABELS: Record<DayType, string> = {
  [DayType.BusinessDay]: "Business day",
  [DayType.CalendarDay]: "Calendar day",
};

export const POD_REQUIREMENT_LABELS: Record<PodRequirementAtStop, string> = {
  [PodRequirementAtStop.None]: "None",
  [PodRequirementAtStop.Photo]: "Photo",
  [PodRequirementAtStop.Signature]: "Signature",
  [PodRequirementAtStop.PhotoOrSignature]: "Photo or Signature",
  [PodRequirementAtStop.PhotoAndSignature]: "Photo and Signature",
};

export const DRIVER_ASSIGNMENT_LABELS: Record<
  ExceptionReasonDriverAssignmentType,
  string
> = {
  [ExceptionReasonDriverAssignmentType.SameDriver]: "Same driver",
  [ExceptionReasonDriverAssignmentType.SameRouteOnlyIfGenerated]:
    "Same route (only if generated)",
  [ExceptionReasonDriverAssignmentType.SameRouteGenerateIfMissing]:
    "Same route (generate)",
  [ExceptionReasonDriverAssignmentType.Unassigned]: "Unassigned",
};

// The tooltip needs a lowercase singular noun mid-sentence ("In 2 business
// days"), which the title-case select label above cannot supply without string
// surgery. Two records, not one derived from the other.
const DAY_TYPE_UNIT_LABELS: Record<DayType, string> = {
  [DayType.BusinessDay]: "business day",
  [DayType.CalendarDay]: "calendar day",
};

export function getReattemptTimingLabel(
  numDays: number,
  dayType: DayType
): string {
  // cube permits numDays === 0; "In 0 business days" would be nonsense.
  if (numDays === 0) return "Same day";
  const dayUnit: string = DAY_TYPE_UNIT_LABELS[dayType] ?? "day";
  return `In ${numDays} ${dayUnit}${numDays === 1 ? "" : "s"}`;
}

export interface ReattemptLadderRow {
  step: string;
  timing: string;
  pod: string;
  driverAssignment: string;
}

// cube caps reattemptConfigs at 10, so the named ordinals cover every real
// case; the fallback exists only so a raised cap cannot render "undefined".
const REATTEMPT_ORDINALS: string[] = [
  "1st",
  "2nd",
  "3rd",
  "4th",
  "5th",
  "6th",
  "7th",
  "8th",
  "9th",
  "10th",
];

function getReattemptOrdinal(index: number): string {
  return REATTEMPT_ORDINALS[index] ?? `${index + 1}th`;
}

// Structured rows for the Force Attempt consequence panel. Deliberately excludes
// the reason's final state: the config table's tooltip has no final-state row,
// and this builder is shared with it.
export function buildReattemptLadder(
  reattemptConfigs: ExceptionReattemptConfig[] | null | undefined
): ReattemptLadderRow[] {
  if (!reattemptConfigs) return [];
  return reattemptConfigs.map((reattemptConfig, index) => ({
    step: `${getReattemptOrdinal(index)} re-attempt`,
    timing: getReattemptTimingLabel(
      reattemptConfig.numDays,
      reattemptConfig.dayType
    ),
    pod:
      POD_REQUIREMENT_LABELS[reattemptConfig.proofOfDeliveryRequirement] ?? "-",
    driverAssignment:
      DRIVER_ASSIGNMENT_LABELS[reattemptConfig.driverAssignmentType] ?? "-",
  }));
}

export function getReattemptConfigsTooltip(
  reattemptConfigs: ExceptionReattemptConfig[] | null | undefined
): string | undefined {
  // undefined rather than "" so React omits the title attribute entirely
  // instead of rendering an empty tooltip on hover.
  if (!reattemptConfigs || reattemptConfigs.length === 0) return undefined;
  return buildReattemptLadder(reattemptConfigs)
    .map(
      (row, index) =>
        `${index + 1}. ${row.timing}, POD ${row.pod}, ${row.driverAssignment}`
    )
    .join("\n");
}
